<script lang="ts" setup>
import SingleSelect from '~/components/form-elements/SingleSelect.vue'
import OrganizationType from '~/enums/organization-type'
import { useDadataStore } from '~/store/dadata'
import type { AutocompleteItem, SelectedItem } from '~/types/form-elements'

const props = defineProps<{
  tinLength: number,
  organizationType: OrganizationType
}>()

const dadataStore = useDadataStore()
const { getOrganizationSuggestions } = storeToRefs(dadataStore)

const autocompleteOrganizations = ref<AutocompleteItem[]>([])
const organizationQuery = ref<string>('')
const debounceForOrganizations = ref<any>(null)

const model = defineModel<SelectedItem | null>()

const emit = defineEmits<{
  (e: 'setOrganization', x: any): void
}>()

watch(organizationQuery, (value) => {
  if (!value) {
    autocompleteOrganizations.value = []

    return
  }

  if (debounceForOrganizations.value) {
    clearTimeout(debounceForOrganizations.value)
  }

  debounceForOrganizations.value = setTimeout(() => {
    if (value.length === props.tinLength) {
      loadOrganizationsData(value)
    }
  }, 500)
})

watch(model, (value: SelectedItem | null): void => {
  if (value === null) {
    autocompleteOrganizations.value = []
    organizationQuery.value = ''
    debounceForOrganizations.value = null
  }
})

async function loadOrganizationsData (_organizationQuery: string): Promise<void> {
  await dadataStore.fetchOrganizationSuggestions(_organizationQuery, props.organizationType)

  if (!_organizationQuery || _organizationQuery === organizationQuery.value) {
    autocompleteOrganizations.value = getOrganizationSuggestions.value.map((x: any): AutocompleteItem => ({
      id: x.id,
      name: props.organizationType === OrganizationType.Legal ? `${x.meta.value} КПП: ${x.attributes.kpp}` : x.meta.value
    }))
  }
}

function updateSelectedOrganization (x: SelectedItem | null, isAdding: boolean): void {
  model.value = x

  if (isAdding) {
    autocompleteOrganizations.value = []
  }

  emit('setOrganization', x)
}

onUnmounted((): void => {
  dadataStore.dispose()
})

defineExpose({ model })
</script>

<template>
  <SingleSelect
    v-model="organizationQuery"
    :autocomplete-items="autocompleteOrganizations"
    is-required
    label-text="ИНН"
    placeholder="Введите ИНН и выберите из списка вашу организацию"
    :selected-item="model"
    @update-selected-item="updateSelectedOrganization"
  />
</template>
